import type {
  AuthoredCard,
  CardLibraryStore,
  DeckLibraryStore,
  HeroDefinition,
  HeroLibraryStore,
} from "../schemas"
import type { SeatInput } from "./engine/match/init"
import type { MatchSetupError } from "./errorMessages"

/**
 * Build a SeatInput from a deck ID, using the card and hero libraries.
 * Returns null + error message if the deck is invalid.
 */
export function buildSeatInput(
  deckId: string,
  seatId: "seat_a" | "seat_b",
  playerId: string,
  displayName: string,
  decks: DeckLibraryStore,
  cards: CardLibraryStore,
  heroes: HeroLibraryStore,
): { input: SeatInput; error: null } | { input: null; error: MatchSetupError } {
  const deckRecord = decks.find((r) => r.deck.deckId === deckId)
  if (!deckRecord) {
    return { input: null, error: { code: "DECK_NOT_FOUND", deckId } }
  }

  const heroRecord = heroes.find(
    (r) => r.hero.heroId === deckRecord.deck.heroId,
  )
  if (!heroRecord)
    return {
      input: null,
      error: { code: "HERO_NOT_FOUND", deckName: deckRecord.deck.name },
    }

  // Build the 24-card merc array from entries
  const cardMap = new Map(cards.map((r) => [r.card.id, r.card]))
  const mercCards: AuthoredCard[] = []
  for (const entry of deckRecord.deck.mercEntries) {
    const card = cardMap.get(entry.cardId)
    if (!card) {
      return {
        input: null,
        error: {
          code: "CARD_NOT_FOUND",
          cardId: entry.cardId,
          deckName: deckRecord.deck.name,
        },
      }
    }
    for (let i = 0; i < entry.count; i++) mercCards.push(card)
  }

  if (mercCards.length !== 24) {
    return {
      input: null,
      error: {
        code: "DECK_WRONG_CARD_COUNT",
        deckName: deckRecord.deck.name,
        count: mercCards.length,
      },
    }
  }

  return {
    input: {
      seatId,
      playerId,
      displayName,
      hero: heroRecord.hero as HeroDefinition,
      mercCards,
    },
    error: null,
  }
}
